/**
 * MKS-410 — API client for the helpdesk REQUEST record.
 *
 * A separate module from `helpdesk.ts` for the same reason the backend keeps
 * `helpdeskRequestRoutes` separate from `helpdeskRoutes`: that one is the
 * cross-project inbox over TASKS (MKS-339), this one is the request record,
 * which is its own table with its own vocabulary. They share the
 * `/api/helpdesk` namespace and nothing else.
 *
 * Shapes mirror `backend/src/api/schemas/helpdeskRequests.ts` verbatim. A
 * field that exists here but not there arrives `undefined` at runtime —
 * `fast-json-stringify` drops undeclared response properties silently — so the
 * two files are edited together.
 */
import { apiGet, apiPost, apiPatch, apiDelete, apiUpload } from './client';

// ---------------------------------------------------------------------------
// Vocabulary — mirrors `services/helpdeskRequestStatus.ts` and
// `services/helpdeskRequestService.ts` on the backend.
// ---------------------------------------------------------------------------

export const HELPDESK_REQUEST_STATUSES = [
  'new',
  'assigned',
  'in_progress',
  'resolved',
  'rejected',
] as const;
export type HelpdeskRequestStatus = (typeof HELPDESK_REQUEST_STATUSES)[number];

/** The three an admin may set directly; routing is what `assigned` means. */
export const ADMIN_SETTABLE_REQUEST_STATUSES = [
  'in_progress',
  'resolved',
  'rejected',
] as const;
export type AdminSettableRequestStatus =
  (typeof ADMIN_SETTABLE_REQUEST_STATUSES)[number];

export const HELPDESK_REQUEST_SOURCES = ['internal', 'client'] as const;
export type HelpdeskRequestSource = (typeof HELPDESK_REQUEST_SOURCES)[number];

export const HELPDESK_REQUEST_PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;
export type HelpdeskRequestPriority = (typeof HELPDESK_REQUEST_PRIORITIES)[number];

export const MAX_REQUEST_TITLE_LENGTH = 200;
export const MAX_REQUEST_DESCRIPTION_LENGTH = 20000;
export const MAX_RESOLUTION_NOTE_LENGTH = 4000;
export const MAX_REJECTION_REASON_LENGTH = 4000;

/** Mirrors the backend attachment policy, so a doomed upload costs no bytes. */
export const MAX_ATTACHMENTS_PER_REQUEST = 10;
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export interface HelpdeskRequestPerson {
  id: number;
  name: string;
  kind: 'user' | 'external_user';
}

export interface HelpdeskRequestRecord {
  id: number;
  source: HelpdeskRequestSource;
  typeId: number;
  typeName: string;
  title: string;
  description: string;
  status: HelpdeskRequestStatus;
  priority: string;
  projectId: number | null;
  projectName: string | null;
  linkedTaskId: number | null;
  categoryId: number | null;
  categoryName: string | null;
  /** The person handling it — "handler" on the reporter's own list. */
  assignee: HelpdeskRequestPerson | null;
  reporter: HelpdeskRequestPerson | null;
  resolutionNote: string | null;
  rejectionReason: string | null;
  resolvedAt: string | null;
  /** Derived: the linked ticket is closed but the ISO record has no note yet. */
  needsResolutionNote: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface HelpdeskRequestType {
  id: number;
  name: string;
  orderIndex: number;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface HelpdeskRequestAttachment {
  id: number;
  requestId: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: number | null;
  uploaderName: string | null;
  createdAt: string;
  downloadUrl: string;
}

// ---------------------------------------------------------------------------
// Filing and reporter reads
// ---------------------------------------------------------------------------

/**
 * The create body carries type, title and description and NOTHING else.
 *
 * The backend rejects an over-posted `projectId` or `assigneeId` with a 400
 * before any handler runs — that refusal is the point of MKS-410 — so this
 * type having no such field is the client-side half of the same rule. Do not
 * widen it: the reporter is deliberately not asked to know which team owns
 * their problem.
 */
export interface CreateHelpdeskRequestInput {
  typeId: number;
  title: string;
  description: string;
}

export function createHelpdeskRequest(input: CreateHelpdeskRequestInput) {
  return apiPost<{ data: HelpdeskRequestRecord }>('/helpdesk/requests', input);
}

export function fetchMyHelpdeskRequests() {
  return apiGet<{ data: HelpdeskRequestRecord[] }>('/helpdesk/my-requests');
}

export function fetchHelpdeskRequest(requestId: number) {
  return apiGet<{ data: HelpdeskRequestRecord }>(`/helpdesk/requests/${requestId}`);
}

export function fetchHelpdeskRequestTypes(includeArchived = false) {
  return apiGet<{ data: HelpdeskRequestType[] }>(
    `/helpdesk/request-types${includeArchived ? '?includeArchived=true' : ''}`,
  );
}

// ---------------------------------------------------------------------------
// The triage queue
// ---------------------------------------------------------------------------

/**
 * The queue's filter set, in exactly the shape the URL carries it.
 *
 * `project` and `assignee` are strings because each holds two different
 * things: a numeric id, or the literal `untriaged` / `unassigned`. That is the
 * backend's own querystring contract — see `helpdeskAdminRequestsQuery`.
 */
export interface HelpdeskRequestFilters {
  status?: string;
  source?: string;
  typeId?: string;
  customerId?: string;
  project?: string;
  assignee?: string;
  needsResolutionNote?: string;
}

/**
 * Drop empty values so the URL never carries `?status=`: the backend's
 * querystring schema enumerates its enums and an empty string is not one of
 * them, which would 400 the whole request for a filter the user just cleared.
 * Same rule as `helpdeskFiltersToQuery` in `helpdesk.ts`.
 */
export function helpdeskRequestFiltersToQuery(filters: HelpdeskRequestFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export function fetchAdminHelpdeskRequests(filters: HelpdeskRequestFilters) {
  return apiGet<{ data: HelpdeskRequestRecord[] }>(
    `/helpdesk/admin/requests${helpdeskRequestFiltersToQuery(filters)}`,
  );
}

// ---------------------------------------------------------------------------
// Triage writes
// ---------------------------------------------------------------------------

/** `assigneeId: null` hands the request back to the unassigned queue. */
export function routeHelpdeskRequestToPerson(requestId: number, assigneeId: number | null) {
  return apiPatch<{ data: HelpdeskRequestRecord }>(
    `/helpdesk/admin/requests/${requestId}/assignee`,
    { assigneeId },
  );
}

export interface RouteToProjectInput {
  projectId: number;
  priority?: string;
  categoryId?: number | null;
}

export function routeHelpdeskRequestToProject(
  requestId: number,
  input: RouteToProjectInput,
) {
  return apiPost<{ data: HelpdeskRequestRecord }>(
    `/helpdesk/admin/requests/${requestId}/route-to-project`,
    input,
  );
}

export interface SetRequestStatusInput {
  status: AdminSettableRequestStatus;
  resolutionNote?: string;
  rejectionReason?: string;
}

export function setHelpdeskRequestStatus(requestId: number, input: SetRequestStatusInput) {
  return apiPost<{ data: HelpdeskRequestRecord }>(
    `/helpdesk/admin/requests/${requestId}/status`,
    input,
  );
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

export function fetchHelpdeskRequestAttachments(requestId: number) {
  return apiGet<{ data: HelpdeskRequestAttachment[] }>(
    `/helpdesk/requests/${requestId}/attachments`,
  );
}

export function uploadHelpdeskRequestAttachment(requestId: number, file: File) {
  return apiUpload<{ data: HelpdeskRequestAttachment }>(
    `/helpdesk/requests/${requestId}/attachments`,
    file,
  );
}

export function deleteHelpdeskRequestAttachment(requestId: number, attachmentId: number) {
  return apiDelete<{ data: { success: boolean } }>(
    `/helpdesk/requests/${requestId}/attachments/${attachmentId}`,
  );
}
